import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";
import SingleSelect from "components/common/SingleSelect";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import { format } from "date-fns";
import { ErrorMessage, Form, Formik, useFormikContext } from "formik";
import { Calendar } from "primereact/calendar";
import { InputSwitch } from "primereact/inputswitch";
import { MultiSelect } from "primereact/multiselect";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { showAlert } from "store/reducers/alertReducer";
import dragIcon from "assets/images/icons/dragIcon.svg";

import { FieldArray } from "formik";
import { CircleX } from "lucide-react";
import FormButtons from "components/common/FormButtons";
import CustomError from "components/common/CustomError";
import Spinners from "components/common/Spinners";
// import CustomSwitch from "components/common/CustomSwitch";
// import TranslateViaAISelection from "components/common/TranslateViaAISelection";
import { updatePlanSchema } from "components/common/Validation";
import {
  getPlanDetails,
  updatePlan,
} from "store/actions/subscriptionPlansSlice";
import { formatPlanType, handleSubmitForm } from "components/common/Utils";

const AddEditPlan = ({ mode }) => {
  const { t } = useTranslation();

  document.title = t("Plans | Work space");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [draggedFeatureIndex, setDraggedFeatureIndex] = useState(null);
  const [dragOverFeatureIndex, setDragOverFeatureIndex] = useState(null);
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const planId = searchParams.get("id");

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const { isLoading, loading, planDetails } = useSelector(
    (state) => state.Plans
  );

  const [formValues, setFormValues] = useState({
    id: "",
    name: "",
    nameAr: "",
    description: "",
    descriptionAr: "",
    highlightDescription: "",
    highlightDescriptionAr: "",
    billingCycle: "",
    trialPeriodDays: 0,
    oneTimeSetupPrice: 0,
    discountInPercentage: 0,
    features:
      planDetails?.features && planDetails?.features.length > 0
        ? planDetails?.features
        : [{ title: "", titleAr: "" }],
    isPopular: false,
    isCustom: false,
  });
  const [datetime12h, setDateTime12h] = useState(null);

  useEffect(() => {
    const getDetails = () => {
      dispatch(getPlanDetails({ id }));
    };
    if (mode === "edit") {
      getDetails();
    }
  }, [mode]);

  useEffect(() => {
    if (planDetails && !loading && mode === "edit") {
      const {
        _id,
        name,
        nameAr,
        description,
        descriptionAr,
        highlightDescription,
        highlightDescriptionAr,
        billingCycle,
        trialPeriodDays,
        price,
        oneTimeSetupPrice,
        usageCount,
        discountInPercentage,
        features,
        isPopular,
        isCustom,
        limits,
      } = planDetails || {};

      setFormValues({
        _id,
        name,
        nameAr,
        description,
        descriptionAr,
        highlightDescription,
        highlightDescriptionAr,
        billingCycle,
        trialPeriodDays,
        price,
        oneTimeSetupPrice,
        usageCount,
        discountInPercentage,
        features,
        limits,
        isPopular,
        isCustom,
        locations: limits?.locations || "",
        members: limits?.members || "",
      });
    }
  }, [planDetails]);

  const handleOnSubmit = (values) => {
    const id = planId;
    const {
      name,
      nameAr,
      description,
      descriptionAr,
      highlightDescription,
      highlightDescriptionAr,
      billingCycle,
      trialPeriodDays,
      price,
      oneTimeSetupPrice,
      discountInPercentage,
      features,
      limits,
      isPopular,
      isCustom,
      locations,
      members,
    } = values;

    const payload = {
      id,
      name,
      nameAr,
      description,
      descriptionAr,
      highlightDescription,
      highlightDescriptionAr,
      billingCycle,
      currency: "SAR",
      trialPeriodDays,
      price,
      limits,
      oneTimeSetupPrice,
      discountInPercentage,
      features: features.map(({ _id, ...rest }) => rest),
      isPopular,
      isCustom,
      limits: {
        locations: Number(locations) || 0,
        members: Number(members) || 0,
      },
    };

    const updatedPayload = {
      ...payload,
    };
    handleSubmitForm({
      dispatch,
      action: updatePlan,
      payload: updatedPayload,
      onSuccess: () => {
        navigate(-1);
      },
    });
  };

  const ScrollToError = () => {
    const { errors, isSubmitting } = useFormikContext();

    useEffect(() => {
      const errorElement = document.querySelector('[aria-invalid="true"]');
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
        errorElement.focus?.();
      }
    }, [isSubmitting, errors]);

    return null;
  };

  const planTypesList = [
    { _id: 1, name: "monthly" },
    { _id: 2, name: "yearly" },
  ];

  // Drag handlers
  const handleFeatureDragStart = (e, index) => {
    setDraggedFeatureIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleFeatureDragOver = (e, index) => {
    e.preventDefault();
    setDragOverFeatureIndex(index);
    e.dataTransfer.dropEffect = "move";
  };

  const handleFeatureDragLeave = () => {
    setDragOverFeatureIndex(null);
  };

  const handleFeatureDrop = (e, dropIndex, setFieldValue, currentFeatures) => {
    e.preventDefault();
    if (draggedFeatureIndex === null || draggedFeatureIndex === dropIndex) {
      setDraggedFeatureIndex(null);
      setDragOverFeatureIndex(null);
      return;
    }

    const newFeatures = [...currentFeatures];
    const draggedItem = newFeatures[draggedFeatureIndex];

    newFeatures.splice(draggedFeatureIndex, 1);
    newFeatures.splice(dropIndex, 0, draggedItem);

    setFieldValue("features", newFeatures);
    setDraggedFeatureIndex(null);
    setDragOverFeatureIndex(null);
  };

  return (
    <React.Fragment>
      <div className="page-content common_page">
        {mode === "edit" && loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle
                title={mode === "add" ? t("Add New Plan") : t("Edit Plan")}
              />
              <Formik
                initialValues={formValues}
                validationSchema={updatePlanSchema}
                enableReinitialize={true}
                onSubmit={handleOnSubmit}
              >
                {({ values, setFieldValue, errors, touched }) => (
                  <Form>
                    <ScrollToError />
                    <div className="common_wrapper">
                      <Row className="mt-2">
                        <Col lg={12}>
                          <div className="bg_white br_14 p_23_30 border_eee">
                            <h2
                              className="fs_20"
                              style={{ marginBottom: "24px" }}
                            >
                              {t("General Plan Info")}
                            </h2>
                            <Row>
                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomLabel
                                    htmlFor="name"
                                    label={t("English Plan Name")}
                                    isRequired
                                  />
                                  <div className="d-flex gap-2 position-relative">
                                    <CustomField
                                      name="name"
                                      type="text"
                                      placeholder={t("Enter English Plan Name")}
                                      touched={touched}
                                      errors={errors}
                                      className={`have_translate_icon`}
                                    />
                                  </div>
                                </div>
                              </Col>

                              <Col lg={6}>
                                <div className="form_field">
                                  <CustomLabel
                                    htmlFor="nameAr"
                                    label={t("Arabic Plan Name")}
                                    isRequired
                                  />
                                  <div className="d-flex gap-2 position-relative">
                                    <CustomField
                                      name="nameAr"
                                      type="text"
                                      placeholder={t("Enter Arabic Plan Name")}
                                      touched={touched}
                                      errors={errors}
                                      isArabic={true}
                                      className={`have_translate_icon`}
                                    />
                                  </div>
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field mt_15">
                                  <CustomLabel
                                    htmlFor="description"
                                    label={t("English Description")}
                                    isRequired
                                  />
                                  <div className="d-flex gap-2 position-relative">
                                    <CustomField
                                      name="description"
                                      type="text"
                                      placeholder={t(
                                        "Enter English Description"
                                      )}
                                      touched={touched}
                                      errors={errors}
                                      className={`have_translate_icon`}
                                    />
                                  </div>
                                </div>
                              </Col>

                              <Col lg={6}>
                                <div className="form_field mt_15">
                                  <CustomLabel
                                    htmlFor="descriptionAr"
                                    label={t("Arabic Description")}
                                    isRequired
                                  />
                                  <div className="d-flex gap-2 position-relative">
                                    <CustomField
                                      name="descriptionAr"
                                      type="text"
                                      placeholder={t(
                                        "Enter Arabic Description"
                                      )}
                                      touched={touched}
                                      errors={errors}
                                      isArabic={true}
                                      className={`have_translate_icon`}
                                    />
                                  </div>
                                </div>
                              </Col>

                              <Col lg={6}>
                                <div className="form_field mt_15">
                                  <CustomLabel
                                    htmlFor="highlightDescription"
                                    label={t("English Highlight Description")}
                                    isRequired
                                  />
                                  <div className="d-flex gap-2 position-relative">
                                    <CustomField
                                      name="highlightDescription"
                                      type="text"
                                      placeholder={t(
                                        "Enter English Highlight Description"
                                      )}
                                      touched={touched}
                                      errors={errors}
                                      className={`have_translate_icon`}
                                    />
                                  </div>
                                </div>
                              </Col>

                              <Col lg={6}>
                                <div className="form_field mt_15">
                                  <CustomLabel
                                    htmlFor="highlightDescriptionAr"
                                    label={t("Arabic Highlight Description")}
                                    isRequired
                                  />
                                  <div className="d-flex gap-2 position-relative">
                                    <CustomField
                                      name="highlightDescriptionAr"
                                      type="text"
                                      placeholder={t(
                                        "Enter Arabic Highlight Description"
                                      )}
                                      touched={touched}
                                      errors={errors}
                                      isArabic={true}
                                      className={`have_translate_icon`}
                                    />
                                  </div>
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field mt_15">
                                  <CustomLabel
                                    htmlFor="locations"
                                    label={t("Locations")}
                                    isRequired
                                  />
                                  <div className="d-flex gap-2 position-relative">
                                    <CustomField
                                      name="locations"
                                      type="text"
                                      placeholder={t("Enter Locations")}
                                      touched={touched}
                                      errors={errors}
                                      className={`have_translate_icon`}
                                    />
                                  </div>
                                </div>
                              </Col>

                              <Col lg={6}>
                                <div className="form_field mt_15">
                                  <CustomLabel
                                    htmlFor="members"
                                    label={t("Members")}
                                    isRequired
                                  />
                                  <div className="d-flex gap-2 position-relative">
                                    <CustomField
                                      name="members"
                                      type="text"
                                      placeholder={t("Enter Members")}
                                      touched={touched}
                                      errors={errors}
                                      className={`have_translate_icon`}
                                    />
                                  </div>
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field mt_15">
                                  <CustomLabel
                                    htmlFor="isPopular"
                                    label={t("Popular")}
                                  />
                                  <br />
                                  <InputSwitch
                                    checked={values.isPopular}
                                    onChange={() =>
                                      setFieldValue(
                                        "isPopular",
                                        !values.isPopular
                                      )
                                    }
                                  />
                                </div>
                              </Col>
                              <Col lg={6}>
                                <div className="form_field mt_15">
                                  <CustomLabel
                                    htmlFor="isCustom"
                                    label={t("Custom")}
                                  />
                                  <br />
                                  <InputSwitch
                                    checked={values.isCustom}
                                    onChange={() =>
                                      setFieldValue(
                                        "isCustom",
                                        !values.isCustom
                                      )
                                    }
                                  />
                                </div>
                              </Col>

                              {/* FOR ONLY PAID PLAN */}

                              <Col lg={6}>
                                <div className=" mt_15">
                                  <CustomLabel
                                    htmlFor="billingCycle"
                                    label={t("Plan Type")}
                                    isRequired
                                  />{" "}
                                </div>
                                <SingleSelect
                                  options={planTypesList?.map((option) => ({
                                    name: formatPlanType(option?.name),
                                    value: option?.name,
                                  }))}
                                  value={values?.billingCycle}
                                  placeholder={t("Plan Type")}
                                  handleSingleSelectChange={(e) => {
                                    setFieldValue("billingCycle", e.value);
                                  }}
                                  className={`custom_single_select ${
                                    errors.billingCycle ? "is-invalid" : ""
                                  }`}
                                  disabled={false}
                                />
                                <CustomError name="category" />
                              </Col>
                              {/* <Col lg={6}></Col> */}
                              {values.trialPeriodDays > 0 && (
                                <>
                                  <Col lg={3}>
                                    <div className="form_field mt_15">
                                      <CustomLabel
                                        htmlFor="trialPeriodDays"
                                        label={t("Trial Duration (In Days)")}
                                        isRequired
                                      />
                                      <CustomField
                                        name="trialPeriodDays"
                                        type="text"
                                        placeholder={t(
                                          "Enter Trial Duration (In Days)"
                                        )}
                                        touched={touched}
                                        errors={errors}
                                      />
                                    </div>
                                  </Col>
                                </>
                              )}
                            </Row>
                          </div>
                        </Col>
                      </Row>

                      {!values.isCustom && (
                        <Row className="mt-3">
                          <Col lg={12}>
                            <div className="bg_white br_14 p_23_30 border_eee">
                              <h2 className="fs_20 mb_24">{t("Pricing")}</h2>
                              <Row>
                                <Col lg={4}>
                                  <div className="form_field">
                                    <CustomLabel
                                      htmlFor="price"
                                      label={t("Price")}
                                      isRequired
                                    />
                                    <CustomField
                                      name="price"
                                      type="text"
                                      placeholder={t("Enter price (e.g., 50)")}
                                      touched={touched}
                                      errors={errors}
                                    />
                                  </div>
                                </Col>

                                <Col lg={4}>
                                  <div className="form_field">
                                    <CustomLabel
                                      htmlFor="discountInPercentage"
                                      label={t("Discount Percentage (%)")}
                                      isRequired
                                    />
                                    <CustomField
                                      name="discountInPercentage"
                                      type="number"
                                      placeholder={t(
                                        "Enter Discount Percentage in %"
                                      )}
                                      touched={touched}
                                      errors={errors}
                                    />
                                  </div>
                                </Col>

                                <Col lg={4}>
                                  <div className="form_field">
                                    <CustomLabel
                                      htmlFor="oneTimeSetupPrice"
                                      label={t("One Time Setup Price")}
                                      isRequired
                                    />
                                    <CustomField
                                      name="oneTimeSetupPrice"
                                      type="number"
                                      placeholder={t(
                                        "Enter minimum number of screens"
                                      )}
                                      touched={touched}
                                      errors={errors}
                                    />
                                  </div>
                                </Col>
                              </Row>
                            </div>
                          </Col>
                        </Row>
                      )}

                      <Row className="mt-3">
                        <Col lg={12}>
                          <div className="bg_white br_14 p_23_30 border_eee">
                            <h2 className="fs_20 ">{t("Features")}</h2>
                            <Row className="p-4">
                              <FieldArray name="features">
                                {({ push, remove }) => (
                                  <>
                                    {values?.features?.map((feature, index) => (
                                      <div
                                        key={index}
                                        className={`feature-container position-relative ${
                                          dragOverFeatureIndex === index
                                            ? "drag-over"
                                            : ""
                                        }`}
                                        draggable
                                        onDragStart={(e) =>
                                          handleFeatureDragStart(e, index)
                                        }
                                        onDragOver={(e) =>
                                          handleFeatureDragOver(e, index)
                                        }
                                        onDragLeave={handleFeatureDragLeave}
                                        onDrop={(e) =>
                                          handleFeatureDrop(
                                            e,
                                            index,
                                            setFieldValue,
                                            values.features
                                          )
                                        }
                                      >
                                        <Row className="">
                                          <Col
                                            lg={1}
                                            className="d-flex justify-content-center drag-icon"
                                          >
                                            <img
                                              src={dragIcon}
                                              alt="drag"
                                              style={{
                                                cursor: "grab",
                                                width: 18,
                                              }}
                                              className="drag-handle"
                                            />
                                          </Col>

                                          <Col lg={5} className="mb-3">
                                            <div className="form_field">
                                              <CustomLabel
                                                htmlFor={`features.${index}.title`}
                                                label={t("Feature Title (EN)")}
                                                isRequired
                                              />
                                              <div className="d-flex gap-2 position-relative">
                                                <CustomField
                                                  name={`features.${index}.title`}
                                                  type="text"
                                                  placeholder={t(
                                                    "Enter feature title in English"
                                                  )}
                                                  touched={touched}
                                                  errors={errors}
                                                  className={`have_translate_icon`}
                                                />
                                              </div>
                                            </div>
                                          </Col>
                                          <Col lg={5} className="mb-3">
                                            <div className="flex-grow-1">
                                              <div className="form_field">
                                                <CustomLabel
                                                  htmlFor={`features.${index}.titleAr`}
                                                  label={t(
                                                    "Feature Title (AR)"
                                                  )}
                                                  isRequired
                                                />
                                                <div className="d-flex gap-2 position-relative">
                                                  <CustomField
                                                    name={`features.${index}.titleAr`}
                                                    type="text"
                                                    placeholder={t(
                                                      "Enter feature title in Arabic"
                                                    )}
                                                    touched={touched}
                                                    errors={errors}
                                                    isArabic={true}
                                                    className={`have_translate_icon`}
                                                  />
                                                </div>
                                              </div>
                                            </div>
                                          </Col>
                                          <Col lg={2}>
                                            <div className="d-flex align-items-start gap-2">
                                              <div className="form_field">
                                                <CustomLabel
                                                  htmlFor="included"
                                                  label={t("Included")}
                                                />
                                                <br />
                                                <InputSwitch
                                                  checked={
                                                    values.features[index]
                                                      .included
                                                  }
                                                  onChange={() =>
                                                    setFieldValue(
                                                      `features.${index}.included`,
                                                      !values.features[index]
                                                        .included
                                                    )
                                                  }
                                                />
                                              </div>
                                              {values.features.length > 1 && (
                                                <div className="close_icon mt_37">
                                                  <CircleX
                                                    className="text_danger c_p"
                                                    onClick={() =>
                                                      remove(index)
                                                    }
                                                  />
                                                </div>
                                              )}
                                            </div>
                                          </Col>
                                        </Row>
                                      </div>
                                    ))}

                                    <Col
                                      lg={12}
                                      className="g_end gap-3 px-0 mt-3"
                                    >
                                      <button
                                        type="button"
                                        className="main_btn"
                                        onClick={() =>
                                          push({ title: "", titleAr: "" })
                                        }
                                      >
                                        + {t("Add More")}
                                      </button>
                                    </Col>
                                  </>
                                )}
                              </FieldArray>
                            </Row>
                          </div>
                        </Col>
                      </Row>

                      <Row className="mt-4">
                        <FormButtons
                          isLoading={isLoading}
                          addBtnText={
                            mode === "add" ? t("Add New") : t("Update")
                          }
                        />
                      </Row>
                    </div>
                  </Form>
                )}
              </Formik>
            </Row>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default AddEditPlan;
